import { Flex, Box, Button, Radio, Group, Alert } from '@mantine/core';
import { IconChevronRight, IconInfoCircle, IconX } from '@tabler/icons-react';
import { GetControlledEntriesResponse } from 'api/actions/get-controlled-entries/get-controlled-entries-response';
import { GetSipDoorsResponse } from 'api/actions/get-sip-doors/get-sip-doors-response';
import { useApi } from 'api/api-context';
import Loader from 'components/Loader';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import panic from 'panic';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RFID_PAGE_PATH, RFID_PAIR_PAGE_PATH } from 'routes/paths';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import P1Regular from 'theme/components/Typography/P1Regular';
import P2Medium from 'theme/components/Typography/P2Medium';

type ControlledEntry = NonNullable<GetControlledEntriesResponse['controlledEntries']>[number];
type SipDoor = NonNullable<GetSipDoorsResponse['sipDoors']>[number];

interface Device {
  id: number;
  deviceId: number;
  name: string;
  type: 'controlledEntry' | 'sipDoor';
}

/**
 * Component for adding a new RFID chip by selecting a device.
 */
const RfidAdd: React.FC = () => {
  const { _t } = useLang();
  const { getAction } = useApi();
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  /**
   * Loads available devices (controlled entries and SIP doors).
   */
  const loadDevices = async () => {
    setLoading(true);
    try {
      const [controlledEntriesRes, sipDoorsRes] = await Promise.all([
        getAction('GetControlledEntries')(),
        getAction('GetSipDoors')(),
      ]);

      const allDevices: Device[] = [];
      controlledEntriesRes.controlledEntries?.forEach((entry: ControlledEntry) => {
        allDevices.push({ id: entry.id, deviceId: entry.deviceId, name: entry.name, type: 'controlledEntry' });
      });
      sipDoorsRes.sipDoors?.forEach((door: SipDoor) => {
        if (door.id && door.deviceId && door.name) {
          allDevices.push({ id: door.id, deviceId: door.deviceId, name: door.name, type: 'sipDoor' });
        }
      });
      setDevices(allDevices);
    } catch (error) {
      panic(error as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, [getAction]);

  /**
   * Handles cancel action and navigates back to RFID page.
   */
  const handleCancel = () => navigate(RFID_PAGE_PATH.original);

  /**
   * Handles continue action and navigates to RFID pairing page.
   */
  const handleContinue = () => {
    if (selectedDeviceId) {
      const device = devices.find((d) => d.deviceId.toString() === selectedDeviceId);
      if (device) {
        navigate(RFID_PAIR_PAGE_PATH.insert({ deviceId: device.deviceId.toString() }));
      }
    }
  };

  return (
    <SimpleLayout>
      <Box>
        <Box p="1rem">
          <Flex gap="0.5rem">
            <H2SemiBold color={theme.colors?.gray?.[12]}>{_t('Add new chip')}</H2SemiBold>
          </Flex>
        </Box>
        <Box px="1rem" pb="1rem">
          <P1Regular color={theme.colors?.gray?.[12]}>
            {_t(
              'Select the intercom through which you want to read the new chip. The chip will then work on all intercoms after adding.'
            )}
          </P1Regular>
        </Box>
        <Box px="1rem" pb="1rem">
          <Alert
            icon={<IconInfoCircle size="24px" />}
            variant="default"
            style={{
              borderRadius: '12px',
              backgroundColor: '#E9EBEC',
              border: `1px solid ${theme.colors?.gray?.[2]}`,
              color: theme.colors?.gray?.[9],
            }}
            styles={{
              message: { color: '#525B6A' },
            }}
          >
            {_t('Move to the selected intercom. After continuing, place the card or chip within 30 seconds.')}
          </Alert>
        </Box>
        {loading ? (
          <Loader />
        ) : (
          <Box px="1rem" pb="10rem">
            <Radio.Group value={selectedDeviceId} onChange={setSelectedDeviceId}>
              <Group gap="md" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                {devices.map((device) => (
                  <Box
                    key={`${device.type}-${device.id}`}
                    p="1rem"
                    bg={theme.white}
                    style={{ borderRadius: '10px', cursor: 'pointer' }}
                    onClick={() => setSelectedDeviceId(device.deviceId.toString())}
                  >
                    <Flex justify="space-between" align="center">
                      <Box>
                        <P2Medium color={theme.colors?.gray?.[6]}>{_t('Intercom:')}</P2Medium>
                        <P1Regular color={theme.colors?.gray?.[12]}>{device.name}</P1Regular>
                      </Box>
                      <Radio
                        value={device.deviceId.toString()}
                        size="md"
                        aria-label={`${_t('Intercom:')} ${device.name}`}
                      />
                    </Flex>
                  </Box>
                ))}
              </Group>
            </Radio.Group>
          </Box>
        )}
      </Box>
      <Flex gap="md" style={{ position: 'fixed', bottom: '1.5rem', left: '1rem', right: '1rem' }}>
        <Button variant="secondary" size="lg" leftSection={<IconX />} onClick={handleCancel} style={{ flex: 1 }}>
          {_t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={handleContinue}
          disabled={!selectedDeviceId}
          style={{ flex: 1 }}
          rightSection={<IconChevronRight />}
        >
          {_t('Continue')}
        </Button>
      </Flex>
    </SimpleLayout>
  );
};

export default RfidAdd;
